import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { getApiInstance } from 'services/Api';

// Thunk to fetch the policy overview PDF
// Returns a serializable dataUrl instead of a Blob to avoid redux non-serializable warnings
export const fetchPolicyOverviewDocument = createAsyncThunk<{ dataUrl: string; fileName: string }>(
	'policyOverviewDocument/fetchPolicyOverviewDocument',
	async () => {
		const apiInstance = await getApiInstance('/commercial-selfservice/');
		// Force responseType 'blob' and correct Accept so backend returns PDF
		const response = await apiInstance.documentApiServiceLayer.fetchPolicyOverviewDocument({
			responseType: 'blob' as any,
			headers: { Accept: 'application/pdf' },
		});
		const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
		// Convert to base64 data URL for serializable storage
		const dataUrl: string = await new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
		return { dataUrl, fileName: 'forsikringsoversigt.pdf' };
	}
);

interface PolicyOverviewDocumentState {
	overviewDataUrl: string | null; // store serializable dataUrl instead of Blob
	status: HttpStatusCode | null;
	error: string | null;
}

const initialState: PolicyOverviewDocumentState = {
	overviewDataUrl: null,
	status: HttpStatusCode.Idle,
	error: null,
};

const policyOverviewDocumentSlice = createSlice({
	name: 'policyOverviewDocument',
	initialState,
	reducers: {
		clearPolicyOverviewDocument(state) {
			state.overviewDataUrl = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPolicyOverviewDocument.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchPolicyOverviewDocument.fulfilled, (state, action) => {
				state.status = HttpStatusCode.OK;
				state.overviewDataUrl = action.payload.dataUrl;
			})
			.addCase(fetchPolicyOverviewDocument.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Forsikringsoversigt download fejlede';
			});
	},
});

export const { clearPolicyOverviewDocument } = policyOverviewDocumentSlice.actions;
export default policyOverviewDocumentSlice.reducer;
